import StateMachine from 'javascript-state-machine';
import { CSDecline, DeclineCodes, UwDecision } from 'shared/constants';
import { userAgentUtil } from '@figure/frontlib-util';

export const HoldingScreenStateMap = {
  APP_REVIEW: 'appReview',
};

export const ErrorStateMap = {
  SSN_CREDIT: 'ssnCredit',
  ERROR_CREDIT: 'errorCredit',
  INCORRECT_PIN: 'incorrectPin',
  NO_CREDIT: 'noCredit',
  CREDIT_SCORE_DECLINE: 'creditScoreDecline',
  DTI_DECLINE: 'dtiDecline',
  DELINQUENCY: 'delinquency',
  FPAY_CS_DECLINE: 'fPayCrossSellDecline',
  FPAY_FIONA_DECLINE: 'fPayFionaDecline',
  DECLINE: 'decline',
};

export const StateMap = {
  ...ErrorStateMap,
  ...HoldingScreenStateMap,
  START: 'start',
  OFFER_SELECTION: 'offerSelection',
  OFFER_RESELECT: 'offerReselect',
  ADDITIONAL_INFO: 'additionalInfo',
  ID_UPLOAD: 'idUpload',
  MANUAL_PAYSTUB_UPLOAD: 'manualPaystubUpload',
  INCOME_VERIFICATION: 'incomeVerification',
  SET_UP_DISBURSEMENT: 'setUpDisbursement',
  SET_UP_AUTOPAY: 'setupAutopay',
  EMPLOYER_VERIFICATION: 'employerVerification',
  REVIEW_DOCS: 'reviewDocs',
  TALK_TO_CSA: 'talkToCsa',
  COMPLETE: 'complete',
  WILDCARD: '*',
};
/**
 * State machine transitions.
 */
const Transition = {
  SELECT_OFFER: 'selectOffer',
  OFFER_SELECTED: 'offerSelected',
  ADDITIONAL_INFO_PROVIDED: 'additionalInfoProvided',
  ID_UPLOADED: 'idUploaded',
  INCOME_PARTIAL_VERIFIED: 'incomePartialVerified',
  INCOME_VERIFIED: 'incomeVerified',
  MANUAL_DISBURSE_SETUP: 'manualDisburseSetup',
  MANUAL_DISBURSE_SETUP_MANUAL_PAY: 'manualDisburseSetupManualPay',
  DISBURSEMENT_SET_UP: 'disbursementSetUp',
  OFFER_RESELECT: 'offerReselect',
  VERIFY_EMPLOYMENT: 'verifyEmployment',
  DOCS_REVIEW: 'docsReview',
  DOCS_REVIEWED: 'docsReviewed',
  COMPLETED: 'completed',
  // Holding Screen
  APP_REVIEW_HOLDING: 'appReviewHolding',
  // Error States
  SOFT_CREDIT_NO_MATCH: 'softCreditNoMatch',
  SOFT_CREDIT_FROZEN: 'softCreditFrozen',
  SOFT_CREDIT_LOCKED: 'softCreditLocked',
  SOFT_CREDIT_INCORRECT_PIN: 'softCreditIncorrectPin',
  SOFT_CREDIT_NO_CREDIT: 'softCreditNoCredit',
  CREDIT_SCORE_DECLINED: 'creditScoreDeclined',
  DTI_DECLINED: 'dtiDeclined',
  DELINQUENCY_REVIEW: 'delinquencyReview',
  FPAY_CS_DECLINED: 'fPayCSDeclined',
  FPAY_FIONA_DECLINED: 'fPayFionaDeclined',
  DECLINED: 'declined',
};

export const fsmConfig = {
  init: StateMap.START,
  transitions: [
    { name: Transition.SELECT_OFFER, from: StateMap.WILDCARD, to: StateMap.OFFER_SELECTION },
    {
      name: Transition.OFFER_SELECTED,
      from: StateMap.OFFER_SELECTION,
      to: StateMap.ADDITIONAL_INFO,
    },
    {
      name: Transition.ADDITIONAL_INFO_PROVIDED,
      from: StateMap.WILDCARD,
      to: StateMap.ID_UPLOAD,
    },
    { name: Transition.ID_UPLOADED, from: StateMap.ID_UPLOAD, to: StateMap.INCOME_VERIFICATION },
    {
      name: Transition.INCOME_PARTIAL_VERIFIED,
      from: StateMap.WILDCARD,
      to: StateMap.MANUAL_PAYSTUB_UPLOAD,
    },
    {
      name: Transition.INCOME_VERIFIED,
      from: StateMap.INCOME_VERIFICATION,
      to: StateMap.SET_UP_DISBURSEMENT,
    },
    {
      name: Transition.MANUAL_DISBURSE_SETUP,
      from: StateMap.WILDCARD,
      to: StateMap.SET_UP_AUTOPAY,
    },
    {
      name: Transition.DISBURSEMENT_SET_UP,
      from: StateMap.SET_UP_DISBURSEMENT,
      to: StateMap.REVIEW_DOCS,
    },
    {
      name: Transition.MANUAL_DISBURSE_SETUP_MANUAL_PAY,
      from: StateMap.WILDCARD,
      to: StateMap.REVIEW_DOCS,
    },
    { name: Transition.OFFER_RESELECT, from: StateMap.WILDCARD, to: StateMap.INCOME_VERIFICATION },
    {
      name: Transition.VERIFY_EMPLOYMENT,
      from: StateMap.REVIEW_DOCS,
      to: StateMap.EMPLOYER_VERIFICATION,
    },
    { name: Transition.DOCS_REVIEWED, from: StateMap.REVIEW_DOCS, to: StateMap.TALK_TO_CSA },
    { name: Transition.COMPLETED, from: StateMap.TALK_TO_CSA, to: StateMap.COMPLETE },
    // Holding Screens
    { name: Transition.APP_REVIEW_HOLDING, from: StateMap.TALK_TO_CSA, to: StateMap.APP_REVIEW },
    // Error states
    {
      name: Transition.SOFT_CREDIT_NO_MATCH,
      from: [StateMap.START, StateMap.OFFER_SELECTION],
      to: StateMap.SSN_CREDIT,
    },
    { name: Transition.SOFT_CREDIT_LOCKED, from: StateMap.WILDCARD, to: StateMap.ERROR_CREDIT },
    { name: Transition.SOFT_CREDIT_FROZEN, from: StateMap.WILDCARD, to: StateMap.ERROR_CREDIT },
    {
      name: Transition.SOFT_CREDIT_INCORRECT_PIN,
      from: StateMap.WILDCARD,
      to: StateMap.INCORRECT_PIN,
    },
    { name: Transition.SOFT_CREDIT_NO_CREDIT, from: StateMap.WILDCARD, to: StateMap.NO_CREDIT },
    {
      name: Transition.CREDIT_SCORE_DECLINED,
      from: StateMap.WILDCARD,
      to: StateMap.CREDIT_SCORE_DECLINE,
    },
    { name: Transition.DTI_DECLINED, from: StateMap.WILDCARD, to: StateMap.DTI_DECLINE },
    { name: Transition.DELINQUENCY_REVIEW, from: StateMap.WILDCARD, to: StateMap.DELINQUENCY },
    { name: Transition.DECLINED, from: StateMap.WILDCARD, to: StateMap.DECLINE },
    { name: Transition.FPAY_CS_DECLINED, from: StateMap.WILDCARD, to: StateMap.FPAY_CS_DECLINE },
    {
      name: Transition.FPAY_FIONA_DECLINED,
      from: StateMap.WILDCARD,
      to: StateMap.FPAY_FIONA_DECLINE,
    },
  ],
};
/**
 * State machine class for the onboarding (application) flow for a mortgage loan.
 */
class PersonalLoanStateMachine extends StateMachine {
  /**
   * State machine states.
   */
  static State = StateMap;

  /**
   * Initialize state machine.
   */
  constructor() {
    super(fsmConfig);
  }

  /**
   * Runs the finite-state-machine and returns the resulting state.
   *
   * @param {Object} profile Primary borrower profile object.
   * @param {Object} personalLoan Personal loan application object.
   * @returns {String} The state machine state after evaluating the entire state machine given the inputs.
   */
  static run = (profile, personalLoan) => {
    const stateMachine = new PersonalLoanStateMachine();

    /**
     * Runs a state machine transition and modifies `stateMachine`s state.
     *
     * @param {String} type The transition type to run.
     */
    const transition = (type) => {
      try {
        stateMachine[type]();
      } catch {
        // Invalid transitions won't be run.
      }
    };

    // Start application -> Offer Select
    if (
      profile.name.uuid &&
      profile.dob &&
      personalLoan.income?.incomePrimary > 0 &&
      personalLoan.consentsList.some((c) => c.type === 'SOFT_CREDIT')
    )
      transition(Transition.SELECT_OFFER);

    if (personalLoan.status?.softCreditNoMatch && !profile.hasSsn)
      transition(Transition.SOFT_CREDIT_NO_MATCH);

    const { selectedOffer } = personalLoan;
    // Offer Select -> Additional Information
    if (selectedOffer?.term > 0 && selectedOffer?.amount > 0) transition(Transition.OFFER_SELECTED);

    // Additional Information -> Application Summary
    const hasHardCreditConsent = personalLoan.consentsList.some((c) => c.type === 'HARD_CREDIT');
    if (profile.hasSsn && hasHardCreditConsent) transition(Transition.ADDITIONAL_INFO_PROVIDED);

    // ID Verify -> Income Verification
    const uploadedID =
      personalLoan.documents.find((d) => d.type === 'DRIVERSLICENSEFRONT') !== undefined ||
      personalLoan.documents.find((d) => d.type === 'IDCARDFRONT') !== undefined ||
      personalLoan.documents.find((d) => d.type === 'PASSPORTFRONT') !== undefined ||
      personalLoan.documents.find((d) => d.type === 'PERMANENTRESIDENTFRONT') !== undefined;
    if (uploadedID) transition(Transition.ID_UPLOADED);

    // Income verified
    if (personalLoan.status?.uwSubDecision === 'VERIFIED_INCOME_MATCHED_OFFER')
      transition(Transition.INCOME_VERIFIED);

    const isSelfEmployed = personalLoan.incomeSources?.some(
      (source) => source.employmentType === 'SELF_EMPLOYED'
    );
    if (
      personalLoan.status?.manualPaystubUpload &&
      !personalLoan.status?.manualPaystubTasksComplete &&
      !isSelfEmployed
    )
      transition(Transition.INCOME_PARTIAL_VERIFIED);

    const linkedDisburseAccount = Boolean(
      personalLoan.disburseAccount?.uuid && personalLoan.disburseAccount?.routingNumber
    );
    const hasAcceptableGiactStatus =
      personalLoan.status?.giactAccountStatus === 'VERIFIED' ||
      personalLoan.status?.giactAccountStatus === 'PENDING_REVIEW';
    const selectedAutopayButNoAutopayAccount =
      personalLoan.payType === 'AUTOPAY' && !personalLoan.autopayAccount.uuid;

    if (linkedDisburseAccount && selectedAutopayButNoAutopayAccount && hasAcceptableGiactStatus) {
      transition(Transition.MANUAL_DISBURSE_SETUP);
    }

    const payAccountComplete =
      linkedDisburseAccount &&
      (personalLoan.payType === 'MANUAL' || personalLoan.autopayAccount?.uuid);
    // Disbusement account set up
    if (payAccountComplete && personalLoan.disburseAccount?.routingNumber)
      transition(Transition.DISBURSEMENT_SET_UP);

    if (linkedDisburseAccount && hasAcceptableGiactStatus && personalLoan.payType === 'MANUAL') {
      transition(Transition.MANUAL_DISBURSE_SETUP_MANUAL_PAY);
    }

    // Applicant needs to reselect offer
    if (personalLoan.status?.uwSubDecision === 'VERIFIED_OFFER')
      transition(Transition.SELECT_OFFER);

    // Reviewed and signed docs
    if (payAccountComplete && personalLoan.status?.disclosurelistClosingComplete)
      transition(Transition.DOCS_REVIEWED);

    if (payAccountComplete && personalLoan.employmentVerificationNeeded)
      transition(Transition.VERIFY_EMPLOYMENT);

    // Holding Screens
    if (personalLoan.status?.krangStatus === 'APP_REVIEW')
      transition(Transition.APP_REVIEW_HOLDING);

    // Error States
    if (personalLoan.status?.softCreditFrozen && !personalLoan.hasFrozenPin)
      transition(Transition.SOFT_CREDIT_FROZEN);
    if (personalLoan.status?.softCreditLocked) transition(Transition.SOFT_CREDIT_LOCKED);
    if (personalLoan.status?.softCreditFrozenWithPin)
      transition(Transition.SOFT_CREDIT_INCORRECT_PIN);
    if (personalLoan.status?.softCreditNoMatchWithSsn) transition(Transition.SOFT_CREDIT_NO_CREDIT);
    if (personalLoan.status?.creditScoreDecline) transition(Transition.CREDIT_SCORE_DECLINE);
    if (personalLoan.status?.showDelinquencyReview) transition(Transition.DELINQUENCY_REVIEW);

    // Declined
    if (
      personalLoan.status?.uwDecision === UwDecision.DECLINE ||
      personalLoan.status?.krangStatus === 'DECLINED'
    ) {
      // Default to DECLINED transition
      transition(Transition.DECLINED);

      const declineCodes = personalLoan.status?.declineCodes;
      const state = profile.addresses?.[0]?.state;
      const figurePayStates = CSDecline.FigurePayStates.map((s) => s.abbr);
      const isMobile = userAgentUtil.isMobileDevice();
      const isfigurePayState = figurePayStates.includes(state);

      // check that decline reasons are NOT in these two lists (length ===  0)
      const isAcceptableFPayDeclineReason =
        declineCodes?.length > 0 &&
        declineCodes.filter((dc) => CSDecline.FPayExcludeReasons.includes(dc)).length === 0;
      const isAcceptableFionaDeclineReason =
        declineCodes?.length > 0 &&
        declineCodes.filter((dc) => CSDecline.FionaExcludeReasons.includes(dc)).length === 0;

      // TODO: Update once backend returns creditScoreDecline and dtiDecline
      const creditScoreDecline = declineCodes.some((dc) => dc === DeclineCodes.VANTAGE_SCORE);
      const dtiDecline = declineCodes.some((dc) => dc === DeclineCodes.DTI);

      if (dtiDecline) transition(Transition.DTI_DECLINED);
      if (creditScoreDecline) transition(Transition.CREDIT_SCORE_DECLINED);

      if (isMobile) {
        if (isfigurePayState && isAcceptableFPayDeclineReason) {
          transition(Transition.FPAY_CS_DECLINED);
        }
        if (!isfigurePayState && isAcceptableFionaDeclineReason) {
          transition(Transition.FPAY_FIONA_DECLINED);
        }
        // defaults to Transition.DECLINED
      } else {
        // isDesktop
        if (isAcceptableFionaDeclineReason) {
          transition(Transition.FPAY_FIONA_DECLINED);
        }
        // defaults to Transition.DECLINED
      }
    }

    // Talked to CSA
    // TODO: Look into what's needed for this
    if (
      personalLoan.status?.krangStatus === 'CSA_CHECKLIST_COMPLETE' || // this will get it's own screen soon
      personalLoan.status?.krangStatus === 'COMPLETE' ||
      personalLoan.status?.krangStatus === 'FUNDABLE'
    ) {
      transition(Transition.COMPLETED);
    }

    return stateMachine.state;
  };
}

export default PersonalLoanStateMachine;
